import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import PendingIcon from "@mui/icons-material/Pending";
import HistoryIcon from "@mui/icons-material/History";

const getStatusConfig = (status, paymentType) => {
  if (paymentType) {
    return {
      label: paymentType.replace(/_/g, " "),
      color:
        paymentType === "investment_fee_paid" ||
        paymentType === "processing_fee_paid"
          ? "#2e7d32"
          : "#d32f2f",
      icon: <CheckCircleIcon sx={{ fontSize: 20 }} />,
      bgColor:
        paymentType === "investment_fee_paid" ||
        paymentType === "processing_fee_paid"
          ? "#edf7ed"
          : "#fdeded",
    };
  }

  const statusMap = {
    incomplete: {
      label: "Incomplete",
      color: "#ed6c02",
      icon: <PendingIcon sx={{ fontSize: 20 }} />,
      bgColor: "#fff4e5",
    },
    info_required: {
      label: "Info Required",
      color: "#ed6c02",
      icon: <InfoIcon sx={{ fontSize: 20 }} />,
      bgColor: "#fff4e5",
    },
    fee_pending: {
      label: "Fee Pending",
      color: "#ed6c02",
      icon: <PendingIcon sx={{ fontSize: 20 }} />,
      bgColor: "#fff4e5",
    },
    approved: {
      label: "Approved",
      color: "#2e7d32",
      icon: <CheckCircleIcon sx={{ fontSize: 20 }} />,
      bgColor: "#edf7ed",
    },
    rejected: {
      label: "Rejected",
      color: "#d32f2f",
      icon: <ErrorIcon sx={{ fontSize: 20 }} />,
      bgColor: "#fdeded",
    },
    pending: {
      label: "Pending",
      color: "#0288d1",
      icon: <HistoryIcon sx={{ fontSize: 20 }} />,
      bgColor: "#e5f6fd",
    },
  };
  return (
    statusMap[status?.toLowerCase()] || {
      label: status || "Unknown",
      color: "#757575",
      icon: <HistoryIcon sx={{ fontSize: 20 }} />,
      bgColor: "#f5f5f5",
    }
  );
};

const StatusTimeline = ({ data, onDetailClick }) => {
  if (!data || data.length === 0) {
    return (
      <div style={{ padding: "32px 0", textAlign: "center" }}>
        <p style={{ color: "#64748b", margin: 0 }}>No history available</p>
      </div>
    );
  }

  return (
    <div>
      {data.map((item, index) => {
        const config = getStatusConfig(item.form_status, item.payment_type);
        const isLast = index === data.length - 1;

        return (
          <div
            key={index}
            style={{
              display: "flex",
              alignItems: "stretch", // Ensures children have same height
            }}
          >
            {/* Left side: Icon and Line */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                marginRight: "16px",
                width: "32px",
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: config.color,
                  zIndex: 1,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                  flexShrink: 0,
                  backgroundColor: config.bgColor,
                }}
              >
                {config.icon}
              </div>
              {!isLast && (
                <div
                  style={{
                    width: "2px",
                    flexGrow: 1,
                    backgroundColor: "#e2e8f0",
                  }}
                />
              )}
            </div>

            {/* Right side: Content Wrapper */}
            <div
              style={{
                flexGrow: 1,
                paddingBottom: isLast ? 0 : "24px",
              }}
            >
              <div
                style={{
                  padding: "12px",
                  borderRadius: "8px",
                  // backgroundColor: config.bgColor,
                  border: "1px solid #e7ecf2ff",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  {/* Status Text*/}
                  <h5
                    style={{
                      // color: config.color,
                      textTransform: "uppercase",
                      fontSize: "16px",
                      margin: 0,
                    }}
                  >
                    {config.label}
                  </h5>

                  {/* Date & Time (Stacked) */}
                  <div style={{ textAlign: "right" }}>
                    <p
                      className="mb-0"
                      style={{
                        fontWeight: 500,
                        fontSize: "12px",
                      }}
                    >
                      {moment(item.createdAt).format("MMM DD, YYYY - hh:mm A")}
                    </p>
                  </div>
                </div>

                <div>
                  {item.note && (
                    <div>
                      <p
                        className="mb-0 mt-1"
                        style={{
                          fontWeight: 600,
                          fontSize: "12px",
                        }}
                      >
                        Reason:
                      </p>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "8px",
                        }}
                      >
                        <div
                          className="note-content"
                          style={{
                            fontSize: "16px",
                            display: "-webkit-box",
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            flexGrow: 1,
                          }}
                          dangerouslySetInnerHTML={{
                            __html: item.note,
                          }}
                        />
                        {(item.form_status === "rejected" ||
                          item.form_status === "pending" ||
                          item.form_status === "info_required") && (
                          <Tooltip title="View full details" placement="bottom">
                            <IconButton
                              size="small"
                              onClick={() => onDetailClick(item)}
                              sx={{
                                color: "#15978a",
                                p: 0.5,
                                backgroundColor: "#f0fdfa",
                                "&:hover": { backgroundColor: "#ccfbf1" },
                              }}
                            >
                              <VisibilityIcon sx={{ fontSize: "16px" }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const StatusHistoryDialog = ({
  open,
  onClose,
  mode,
  findData,
  selectedNote,
  openBy = false,
  onBackToHistory,
  onViewDetail,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: "400px",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          backgroundColor: "#f8fafc",
          overflow: "hidden",
        },
      }}
    >
      <style>
        {`
          .note-content p {
            margin-bottom: 0px !important;
          }
        `}
      </style>
      <DialogTitle
        sx={{
          backgroundColor: "#15978a",
          color: "#fff",
          p: "12px 24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h3 className="mb-0">
          {mode === "history"
            ? "Status History"
            : (openBy ? selectedNote : findData)?.form_status ===
              "info_required"
            ? "Required Information"
            : "Rejection Details"}
        </h3>
        {!(mode === "reason" && openBy) && (
          <IconButton size="small" onClick={onClose} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <div style={{ maxHeight: "60vh", overflowY: "auto", padding: "24px" }}>
          {mode === "history" ? (
            <StatusTimeline
              data={findData?.form_history || []}
              onDetailClick={(item) => {
                if (onViewDetail) {
                  onViewDetail(item);
                }
              }}
            />
          ) : (
            <div
              className="note-content"
              style={{
                color: "#1e293b",
                fontSize: "16px",
                lineHeight: 1.8,
              }}
              dangerouslySetInnerHTML={{
                __html: openBy ? selectedNote?.note : findData?.note,
              }}
            />
          )}
        </div>
      </DialogContent>

      <DialogActions sx={{ p: "12px 24px", borderTop: "1px solid #dee2e6" }}>
        {mode === "reason" && openBy ? (
          <Button onClick={onBackToHistory} variant="outlined">
            Back to History
          </Button>
        ) : (
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default StatusHistoryDialog;
